import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  HomeIcon,
  LedgerIcon,
  PiggyIcon,
  RecurringIcon,
  SettingsIcon,
} from './TabIcons';

const TABS = [
  { to: '/', label: 'Accueil', Icon: HomeIcon },
  { to: '/ecritures', label: 'Écritures', Icon: LedgerIcon },
  { to: '/epargne', label: 'Épargne', Icon: PiggyIcon },
  { to: '/fixes', label: 'Revenus et charges fixes', Icon: RecurringIcon },
  { to: '/reglages', label: 'Réglages', Icon: SettingsIcon },
];

interface Action {
  to: string;
  label: string;
  /** Classes écrites en toutes lettres : Tailwind ne devine pas les noms construits. */
  tone: string;
}

/**
 * Le bouton d'action change de sens selon l'écran, et le dit : une couleur
 * propre à chaque contexte, et un libellé accessible qui nomme ce qu'il crée.
 *
 * Il passe par l'URL plutôt que par un état partagé — le bouton Retour referme
 * alors le formulaire, ce qu'un utilisateur d'Android attend.
 */
const ACTIONS: Record<string, Action> = {
  '/': {
    to: '/saisie',
    label: 'Saisir une écriture',
    tone: 'bg-fab-entry text-fab-entry-ink',
  },
  '/ecritures': {
    to: '/saisie',
    label: 'Saisir une écriture',
    tone: 'bg-fab-entry text-fab-entry-ink',
  },
  '/epargne': {
    to: '/epargne?nouveau=1',
    label: 'Ajouter un pôle d’épargne',
    tone: 'bg-fab-savings text-fab-savings-ink',
  },
  '/fixes': {
    to: '/fixes?nouveau=1',
    label: 'Ajouter un revenu ou une charge fixe',
    tone: 'bg-fab-fixed text-fab-fixed-ink',
  },
  '/comptes': {
    to: '/comptes?nouveau=1',
    label: 'Ajouter un compte',
    tone: 'bg-fab-accounts text-fab-accounts-ink',
  },
};

export function TabBar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Réglages n'a rien à créer : un bouton sans objet vaut moins que pas de bouton.
  const action = ACTIONS[pathname];

  return (
    <>
      {action !== undefined && (
        <button
          type="button"
          onClick={() => void navigate(action.to)}
          aria-label={action.label}
          className={`fixed right-5 bottom-24 z-10 h-14 w-14 rounded-full text-3xl leading-none shadow-lg ${action.tone}`}
        >
          +
        </button>
      )}

      <nav className="safe-bottom fixed inset-x-0 bottom-0 z-10 border-t border-line bg-surface">
        <ul className="mx-auto flex max-w-md">
          {TABS.map((tab) => (
            <li key={tab.to} className="flex-1">
              {/* Sans libellé visible, le nom doit exister pour les lecteurs
                  d'écran — et le survol le rappelle à la souris. */}
              <NavLink
                to={tab.to}
                end={tab.to === '/'}
                aria-label={tab.label}
                title={tab.label}
                className={({ isActive }) =>
                  `flex flex-col items-center gap-1.5 pt-2 pb-3 ${
                    isActive ? 'text-series' : 'text-ink-muted'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {/* L'onglet actif ne se signale pas par la seule couleur :
                        la barre est un second indice, perceptible sans elle. */}
                    <span
                      aria-hidden="true"
                      className={`h-1 w-6 rounded-full ${isActive ? 'bg-series' : 'bg-transparent'}`}
                    />
                    <tab.Icon />
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </>
  );
}
